import React, { useState, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Heart, Laptop, Briefcase, Palette,
  Wrench, Leaf, Shield, FlaskConical, Target, MapPin, Clock,
  DollarSign, GraduationCap, CheckCircle, ArrowRight, BookOpen,
  Lightbulb, Users, Home, Star, AlertCircle, RefreshCw, Building2,
  Brain, TrendingUp, Printer, Download, Share2, BarChart3,
  Eye, Headphones, FileText, Hand, Sparkles, MessageSquare, Scale
} from 'lucide-react';

// Import Jamaica-specific career data
import careerData from '../data/career-paths-jamaica.json';

const BackButton = ({ onClick, label = "Back" }) => (
  <button onClick={onClick} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 font-medium">
    <ChevronLeft className="w-5 h-5" />{label}
  </button>
);

// Icon mapping
const iconMap = {
  Heart, Laptop, Briefcase, Palette, Wrench, Leaf, Shield, Flask: FlaskConical
};

// Progress indicator with sections
const ProgressBar = ({ current, total, sectionName }) => (
  <div className="mb-6">
    <div className="flex justify-between items-center text-sm mb-2">
      <span className="text-brand-700 font-medium">{sectionName}</span>
      <span className="text-gray-500">Step {current} of {total}</span>
    </div>
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-brand-500 to-brand-500 transition-all duration-500"
        style={{ width: `${(current / total) * 100}%` }}
      />
    </div>
  </div>
);

// Rating scale component for work values and activities
const RatingScale = ({ question, description, value, onChange, options }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-5 mb-3 sm:mb-4">
    <h3 className="font-semibold text-gray-800 mb-1 text-sm sm:text-base">{question}</h3>
    {description && <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4">{description}</p>}
    <div className="flex gap-1.5 sm:gap-2 flex-wrap">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
            value === option.value
              ? 'bg-brand-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

// Section header component
const SectionHeader = ({ icon: Icon, title, description, color = "emerald" }) => (
  <div className={`bg-gradient-to-r from-${color}-500 to-${color}-600 rounded-xl p-4 sm:p-6 text-white mb-4 sm:mb-6`}>
    <div className="flex items-center gap-2 sm:gap-3 mb-2">
      <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
      </div>
      <h2 className="text-xl sm:text-2xl font-bold">{title}</h2>
    </div>
    <p className="text-white/90 text-sm sm:text-base">{description}</p>
  </div>
);

export const CareerPathVisualiser = ({ setPage, goBack }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    // Basic interests and preferences
    interests: [],
    environment: null,
    people: null,
    stability: null,
    education_time: null,
    location: null,
    // Work values
    work_values: {
      achievement: null,
      independence: null,
      recognition: null,
      relationships: null,
      support: null,
      working_conditions: null
    },
    // Work activities (data/people/things)
    work_activities: {
      data_complexity: null,
      data_research: null,
      people_helping: null,
      people_teaching: null,
      people_persuading: null,
      things_operating: null,
      things_building: null
    },
    // Skills self-assessment
    skills: {
      verbal: null,
      numerical: null,
      spatial: null,
      manual: null,
      creative: null,
      social: null,
      analytical: null,
      organizational: null
    },
    // Learning style
    learning_style: {
      visual: null,
      auditory: null,
      reading: null,
      kinesthetic: null
    }
  });
  const [results, setResults] = useState(null);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const resultsRef = useRef(null);

  // Define sections and steps
  const sections = [
    { name: "Welcome", steps: 1 },
    { name: "Your Interests", steps: 1 },
    { name: "Work Environment", steps: 2 },
    { name: "Work Values", steps: 1 },
    { name: "Work Activities", steps: 1 },
    { name: "Your Skills", steps: 1 },
    { name: "Learning Style", steps: 1 },
    { name: "Practical Factors", steps: 2 }
  ];

  const totalSteps = sections.reduce((sum, s) => sum + s.steps, 0);

  const getCurrentSection = () => {
    let stepCount = 0;
    for (let i = 0; i < sections.length; i++) {
      stepCount += sections[i].steps;
      if (step < stepCount) return sections[i].name;
    }
    return sections[sections.length - 1].name;
  };

  const handleInterestToggle = (interestId) => {
    setAnswers(prev => ({
      ...prev,
      interests: prev.interests.includes(interestId)
        ? prev.interests.filter(i => i !== interestId)
        : [...prev.interests, interestId].slice(0, 3)
    }));
  };

  const handleAnswer = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const handleNestedAnswer = (category, field, value) => {
    setAnswers(prev => ({
      ...prev,
      [category]: { ...prev[category], [field]: value }
    }));
  };

  const nextStep = () => {
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      calculateResults();
    }
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const calculateResults = () => {
    const scoredCareers = careerData.careers.map(career => {
      let score = 0;
      let matchReasons = [];
      let matchDetails = {
        interests: 0,
        workStyle: 0,
        workValues: 0,
        workActivities: 0,
        skills: 0,
        learningStyle: 0
      };

      // 1. Interest matching (30% weight)
      const interestMatches = career.interest_match.filter(i => answers.interests.includes(i));
      const interestScore = interestMatches.length * 25;
      matchDetails.interests = interestScore;
      score += interestScore;
      if (interestMatches.length > 0) {
        matchReasons.push(`Matches your interest in ${interestMatches.map(i =>
          careerData.interest_areas.find(ia => ia.id === i)?.label
        ).join(' and ')}`);
      }

      // 2. Work style matching (15% weight)
      let styleScore = 0;
      if (career.work_style_match.environment?.includes(answers.environment)) {
        styleScore += 10;
        matchReasons.push('Work environment fits your preference');
      }
      if (career.work_style_match.people?.includes(answers.people)) {
        styleScore += 10;
        matchReasons.push('Team style matches what you want');
      }
      if (career.work_style_match.stability?.includes(answers.stability)) {
        styleScore += 10;
        matchReasons.push('Aligns with your priorities');
      }
      matchDetails.workStyle = styleScore;
      score += styleScore;

      // 3. Work values matching (15% weight)
      if (career.work_values_match) {
        let valuesScore = 0;
        let valuesMatched = 0;
        Object.entries(career.work_values_match).forEach(([key, careerValue]) => {
          const userValue = answers.work_values[key];
          if (userValue !== null) {
            const diff = Math.abs(careerValue - userValue);
            if (diff <= 1) {
              valuesScore += 5;
              valuesMatched++;
            }
          }
        });
        matchDetails.workValues = valuesScore;
        score += valuesScore;
        if (valuesMatched >= 2) {
          matchReasons.push('Your work values align well');
        }
      }

      // 4. Work activities matching (15% weight)
      if (career.work_activities_match) {
        let activitiesScore = 0;
        const dataScore = (answers.work_activities.data_complexity || 0) + (answers.work_activities.data_research || 0);
        const peopleScore = (answers.work_activities.people_helping || 0) + (answers.work_activities.people_teaching || 0) + (answers.work_activities.people_persuading || 0);
        const thingsScore = (answers.work_activities.things_operating || 0) + (answers.work_activities.things_building || 0);

        const userDataPref = dataScore / 10;
        const userPeoplePref = peopleScore / 15;
        const userThingsPref = thingsScore / 10;

        const careerData = (career.work_activities_match.data || 3) / 5;
        const careerPeople = (career.work_activities_match.people || 3) / 5;
        const careerThings = (career.work_activities_match.things || 3) / 5;

        if (Math.abs(userDataPref - careerData) < 0.3) activitiesScore += 8;
        if (Math.abs(userPeoplePref - careerPeople) < 0.3) activitiesScore += 8;
        if (Math.abs(userThingsPref - careerThings) < 0.3) activitiesScore += 8;

        matchDetails.workActivities = activitiesScore;
        score += activitiesScore;
        if (activitiesScore >= 16) {
          matchReasons.push('Work activities match your preferences');
        }
      }

      // 5. Skills matching (15% weight)
      if (career.skills_required) {
        let skillsScore = 0;
        let skillsMatched = 0;
        Object.entries(career.skills_required).forEach(([skill, requiredLevel]) => {
          const userLevel = answers.skills[skill];
          if (userLevel !== null && userLevel >= requiredLevel - 1) {
            skillsScore += 4;
            skillsMatched++;
          }
        });
        matchDetails.skills = skillsScore;
        score += skillsScore;
        if (skillsMatched >= 3) {
          matchReasons.push('Your skills match well');
        }
      }

      // 6. Learning style fit (10% weight)
      if (career.learning_style_fit) {
        let learningScore = 0;
        const userTopStyles = Object.entries(answers.learning_style)
          .filter(([_, v]) => v !== null && v >= 4)
          .map(([k]) => k);

        const styleMatch = career.learning_style_fit.some(s => userTopStyles.includes(s));
        if (styleMatch) {
          learningScore = 15;
          matchReasons.push('Training style suits how you learn');
        }
        matchDetails.learningStyle = learningScore;
        score += learningScore;
      }

      // 7. Education time constraint bonus
      if (answers.education_time === 'quick') {
        const hasQuickPath = career.pathways?.some(p => p.duration_years <= 1.5);
        if (hasQuickPath) {
          score += 15;
          matchReasons.push('Has a fast-track training option');
        } else {
          score -= 10;
        }
      } else if (answers.education_time === 'parttime') {
        const hasPartTimePath = career.pathways?.some(p =>
          p.type === 'tvet' || p.type === 'self_taught' || p.type === 'entry_level'
        );
        if (hasPartTimePath) {
          score += 10;
          matchReasons.push('Can study while working');
        }
      }

      return {
        ...career,
        score,
        matchReasons,
        matchDetails,
        matchPercentage: Math.min(100, Math.round((score / 150) * 100))
      };
    });

    const topCareers = scoredCareers
      .sort((a, b) => b.score - a.score)
      .slice(0, 8);

    setResults(topCareers);
  };

  const restartQuiz = () => {
    setStep(0);
    setAnswers({
      interests: [],
      environment: null,
      people: null,
      stability: null,
      education_time: null,
      location: null,
      work_values: {
        achievement: null,
        independence: null,
        recognition: null,
        relationships: null,
        support: null,
        working_conditions: null
      },
      work_activities: {
        data_complexity: null,
        data_research: null,
        people_helping: null,
        people_teaching: null,
        people_persuading: null,
        things_operating: null,
        things_building: null
      },
      skills: {
        verbal: null,
        numerical: null,
        spatial: null,
        manual: null,
        creative: null,
        social: null,
        analytical: null,
        organizational: null
      },
      learning_style: {
        visual: null,
        auditory: null,
        reading: null,
        kinesthetic: null
      }
    });
    setResults(null);
    setSelectedCareer(null);
  };

  const handlePrint = () => {
    window.print();
  };

  // Step 0: Welcome
  const WelcomeStep = () => (
    <div className="text-center max-w-2xl mx-auto px-2 sm:px-0">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-brand-400 to-brand-500 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
        <Target className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
      </div>
      <h1 className="text-2xl sm:text-3xl font-black text-gray-800 mb-3 sm:mb-4">
        Career Path Explorer
      </h1>
      <p className="text-base sm:text-lg text-gray-600 mb-4 sm:mb-6">
        Discover careers that match your interests, values, skills, and learning style.
        This comprehensive assessment will help you explore career options based on who you are.
      </p>

      <div className="bg-brand-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8 text-left">
        <h3 className="font-bold text-brand-800 mb-3 flex items-center gap-2 text-sm sm:text-base">
          <Lightbulb className="w-5 h-5 flex-shrink-0" />
          What we'll explore together:
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-brand-700 text-sm sm:text-base">
              <Heart className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Your Interests</strong> - What excites you</span>
            </div>
            <div className="flex items-start gap-2 text-brand-700 text-sm sm:text-base">
              <Scale className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Work Values</strong> - What matters to you</span>
            </div>
            <div className="flex items-start gap-2 text-brand-700 text-sm sm:text-base">
              <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Work Activities</strong> - How you like to work</span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-2 text-brand-700 text-sm sm:text-base">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Your Skills</strong> - What you're good at</span>
            </div>
            <div className="flex items-start gap-2 text-brand-700 text-sm sm:text-base">
              <Brain className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Learning Style</strong> - How you learn best</span>
            </div>
            <div className="flex items-start gap-2 text-brand-700 text-sm sm:text-base">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span><strong>Practical Factors</strong> - Your situation</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
        <p className="text-blue-700 text-xs sm:text-sm">
          <strong>Based on Job Bank Canada's career exploration framework</strong> - adapted for Jamaica with local career data, salaries, and training pathways.
        </p>
      </div>

      <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
        This takes about 10-15 minutes. There are no wrong answers - just be honest about yourself.
      </p>
      <button
        onClick={nextStep}
        className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-brand-600 to-brand-600 text-white rounded-xl font-bold text-base sm:text-lg hover:shadow-lg transition-all"
      >
        Start Exploration
      </button>
    </div>
  );

  // Step 1: Interests
  const InterestsStep = () => (
    <div>
      <SectionHeader
        icon={Heart}
        title="Your Interests"
        description="Choose up to 3 areas that genuinely excite you. Don't think about what's practical - follow your curiosity."
      />

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-6 sm:mb-8">
        {careerData.interest_areas.map(interest => {
          const isSelected = answers.interests.includes(interest.id);
          const IconComponent = iconMap[interest.icon] || Heart;
          return (
            <button
              key={interest.id}
              onClick={() => handleInterestToggle(interest.id)}
              className={`p-3 sm:p-4 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? 'border-brand-500 bg-brand-50 shadow-md'
                  : 'border-gray-200 hover:border-brand-300 bg-white'
              }`}
            >
              <IconComponent className={`w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 ${isSelected ? 'text-brand-600' : 'text-gray-400'}`} />
              <div className={`font-semibold text-sm sm:text-base ${isSelected ? 'text-brand-800' : 'text-gray-800'}`}>
                {interest.label}
              </div>
              <div className="text-xs text-gray-500 mt-1 line-clamp-2">{interest.description}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
        <p className="text-gray-600 text-sm sm:text-base">
          Selected: <span className="font-bold text-brand-600">{answers.interests.length}/3</span>
          {answers.interests.length === 0 && <span className="text-gray-400 ml-2">(select at least 1)</span>}
        </p>
      </div>
    </div>
  );

  // Step 2: Work Environment
  const EnvironmentStep = () => (
    <div>
      <SectionHeader
        icon={Building2}
        title="Work Environment"
        description="Think about where you'd feel most comfortable working day-to-day."
      />

      <div className="space-y-2 sm:space-y-3">
        {[
          { value: 'office', label: 'In an office with air conditioning', icon: Building2, desc: 'Desk work, computer, indoor environment' },
          { value: 'outdoor', label: 'Outside, even in the sun', icon: Leaf, desc: 'Construction, farming, fieldwork' },
          { value: 'varied', label: 'Different places each day', icon: MapPin, desc: 'Visiting clients, different sites, travel' },
          { value: 'home', label: 'From home if possible', icon: Home, desc: 'Remote work, flexible location' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => handleAnswer('environment', option.value)}
            className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left flex items-center gap-3 sm:gap-4 ${
              answers.environment === option.value
                ? 'border-brand-500 bg-brand-50'
                : 'border-gray-200 hover:border-brand-300 bg-white'
            }`}
          >
            <option.icon className={`w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0 ${answers.environment === option.value ? 'text-brand-600' : 'text-gray-400'}`} />
            <div className="min-w-0">
              <div className="font-semibold text-gray-800 text-sm sm:text-base">{option.label}</div>
              <div className="text-xs sm:text-sm text-gray-500">{option.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Step 3: People
  const PeopleStep = () => (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">How do you prefer to work with others?</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">There's no right answer - some people thrive with others, some prefer independence.</p>

      <div className="space-y-2 sm:space-y-3">
        {[
          { value: 'many', label: 'With lots of people every day', icon: Users, desc: 'Customer-facing, teamwork, social environment' },
          { value: 'team', label: 'With a small team I know well', icon: Users, desc: 'Collaborative projects, familiar colleagues' },
          { value: 'alone', label: 'Mostly by myself', icon: Target, desc: 'Independent work, focused tasks' },
          { value: 'mix', label: 'A mix depending on the task', icon: RefreshCw, desc: 'Some solo work, some collaboration' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => handleAnswer('people', option.value)}
            className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left flex items-center gap-3 sm:gap-4 ${
              answers.people === option.value
                ? 'border-brand-500 bg-brand-50'
                : 'border-gray-200 hover:border-brand-300 bg-white'
            }`}
          >
            <option.icon className={`w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0 ${answers.people === option.value ? 'text-brand-600' : 'text-gray-400'}`} />
            <div className="min-w-0">
              <div className="font-semibold text-gray-800 text-sm sm:text-base">{option.label}</div>
              <div className="text-xs sm:text-sm text-gray-500">{option.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Step 4: Work Values
  const WorkValuesStep = () => (
    <div>
      <SectionHeader
        icon={Scale}
        title="Work Values"
        description="Rate how important each of these factors is to you in a job. Be honest - there are no wrong answers."
      />

      {careerData.work_values_questions?.map(q => (
        <RatingScale
          key={q.id}
          question={q.question}
          description={q.description}
          value={answers.work_values[q.id]}
          onChange={(val) => handleNestedAnswer('work_values', q.id, val)}
          options={q.options}
        />
      ))}
    </div>
  );

  // Step 5: Work Activities
  const WorkActivitiesStep = () => (
    <div>
      <SectionHeader
        icon={BarChart3}
        title="Work Activities"
        description="Different jobs involve working with data, people, and things in different ways. Rate how much you enjoy each activity."
      />

      <div className="mb-6">
        <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
          <Laptop className="w-5 h-5 text-blue-600" />
          Working with Data & Information
        </h3>
        {careerData.work_activities_questions?.filter(q => q.category === 'data').map(q => (
          <RatingScale
            key={q.id}
            question={q.question}
            description={q.description}
            value={answers.work_activities[q.id]}
            onChange={(val) => handleNestedAnswer('work_activities', q.id, val)}
            options={q.options}
          />
        ))}
      </div>

      <div className="mb-6">
        <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-brand-600" />
          Working with People
        </h3>
        {careerData.work_activities_questions?.filter(q => q.category === 'people').map(q => (
          <RatingScale
            key={q.id}
            question={q.question}
            description={q.description}
            value={answers.work_activities[q.id]}
            onChange={(val) => handleNestedAnswer('work_activities', q.id, val)}
            options={q.options}
          />
        ))}
      </div>

      <div>
        <h3 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
          <Wrench className="w-5 h-5 text-amber-600" />
          Working with Things
        </h3>
        {careerData.work_activities_questions?.filter(q => q.category === 'things').map(q => (
          <RatingScale
            key={q.id}
            question={q.question}
            description={q.description}
            value={answers.work_activities[q.id]}
            onChange={(val) => handleNestedAnswer('work_activities', q.id, val)}
            options={q.options}
          />
        ))}
      </div>
    </div>
  );

  // Step 6: Skills Self-Assessment
  const SkillsStep = () => (
    <div>
      <SectionHeader
        icon={Sparkles}
        title="Your Skills & Abilities"
        description="Rate your current abilities honestly. This isn't about what you want to be good at - it's about where you are now."
      />

      {careerData.skills_assessment?.map(skill => (
        <RatingScale
          key={skill.id}
          question={skill.question}
          description={skill.description}
          value={answers.skills[skill.id]}
          onChange={(val) => handleNestedAnswer('skills', skill.id, val)}
          options={skill.options}
        />
      ))}
    </div>
  );

  // Step 7: Learning Style
  const LearningStyleStep = () => (
    <div>
      <SectionHeader
        icon={Brain}
        title="How You Learn Best"
        description="Different training programs teach in different ways. Understanding how you learn helps match you with the right path."
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {[
          { id: 'visual', icon: Eye, label: 'Visual Learning', desc: 'Pictures, diagrams, videos' },
          { id: 'auditory', icon: Headphones, label: 'Auditory Learning', desc: 'Lectures, discussions, audio' },
          { id: 'reading', icon: FileText, label: 'Reading/Writing', desc: 'Books, manuals, notes' },
          { id: 'kinesthetic', icon: Hand, label: 'Hands-On Learning', desc: 'Practice, experiments, doing' }
        ].map(style => {
          const question = careerData.learning_style_questions?.find(q => q.id === style.id);
          return (
            <div key={style.id} className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <style.icon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                </div>
                <div className="min-w-0">
                  <h4 className="font-semibold text-gray-800 text-sm sm:text-base">{style.label}</h4>
                  <p className="text-xs text-gray-500">{style.desc}</p>
                </div>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">{question?.question}</p>
              <div className="flex gap-1 flex-wrap">
                {question?.options.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleNestedAnswer('learning_style', style.id, opt.value)}
                    className={`px-2 sm:px-3 py-1 rounded text-xs font-medium transition-all ${
                      answers.learning_style[style.id] === opt.value
                        ? 'bg-purple-600 text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Step 8: Career Priorities
  const StabilityStep = () => (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">What matters most to you in a career?</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Be honest with yourself. All of these are valid priorities.</p>

      <div className="space-y-2 sm:space-y-3">
        {[
          { value: 'stable', label: 'A steady, predictable income', icon: Shield, desc: 'Job security, reliable pay, less risk' },
          { value: 'growth', label: 'Opportunity to earn more over time', icon: TrendingUp, desc: 'Career advancement, increasing salary' },
          { value: 'flexible', label: 'Flexible hours and freedom', icon: Clock, desc: 'Control over your schedule, work-life balance' },
          { value: 'purpose', label: 'Meaningful work that helps others', icon: Heart, desc: 'Making a difference, even if pay is less' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => handleAnswer('stability', option.value)}
            className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left flex items-center gap-3 sm:gap-4 ${
              answers.stability === option.value
                ? 'border-brand-500 bg-brand-50'
                : 'border-gray-200 hover:border-brand-300 bg-white'
            }`}
          >
            <option.icon className={`w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0 ${answers.stability === option.value ? 'text-brand-600' : 'text-gray-400'}`} />
            <div className="min-w-0">
              <div className="font-semibold text-gray-800 text-sm sm:text-base">{option.label}</div>
              <div className="text-xs sm:text-sm text-gray-500">{option.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Step 9: Education Timeline
  const EducationTimeStep = () => (
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">How much time can you spend on education or training?</h2>
      <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Be realistic about your situation. There are good careers for every timeline.</p>

      <div className="space-y-2 sm:space-y-3">
        {[
          { value: 'quick', label: 'I need to start earning within a year', icon: Clock, desc: 'Short training programs, quick entry to work' },
          { value: 'medium', label: 'I can study for 2-3 years', icon: BookOpen, desc: 'Diploma, HEART programs, some degrees' },
          { value: 'long', label: 'I can commit to 4+ years', icon: GraduationCap, desc: 'University degree, professional qualifications' },
          { value: 'parttime', label: 'I need to work while I study', icon: Briefcase, desc: 'Part-time programs, evening classes' }
        ].map(option => (
          <button
            key={option.value}
            onClick={() => handleAnswer('education_time', option.value)}
            className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left flex items-center gap-3 sm:gap-4 ${
              answers.education_time === option.value
                ? 'border-brand-500 bg-brand-50'
                : 'border-gray-200 hover:border-brand-300 bg-white'
            }`}
          >
            <option.icon className={`w-6 h-6 sm:w-8 sm:h-8 flex-shrink-0 ${answers.education_time === option.value ? 'text-brand-600' : 'text-gray-400'}`} />
            <div className="min-w-0">
              <div className="font-semibold text-gray-800 text-sm sm:text-base">{option.label}</div>
              <div className="text-xs sm:text-sm text-gray-500">{option.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Career detail view
  const CareerDetailView = ({ career }) => (
    <div className="space-y-4 sm:space-y-6">
      <BackButton onClick={() => setSelectedCareer(null)} label="Back to Results" />

      {/* Header */}
      <div className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2 sm:mb-3">
          <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full">{career.category}</span>
          <span className="text-xs sm:text-sm bg-white/30 px-2 sm:px-3 py-1 rounded-full font-bold">
            {career.matchPercentage}% Match
          </span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black mt-2 sm:mt-3">{career.title}</h1>
        <p className="text-brand-100 mt-2 text-sm sm:text-base">{career.description}</p>
        {career.also_called && (
          <p className="text-brand-200 text-xs sm:text-sm mt-2">Also known as: {career.also_called.join(', ')}</p>
        )}
      </div>

      {/* Match Analysis */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600" />
          Why This Matches You
        </h3>
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {career.matchReasons?.slice(0, 5).map((reason, i) => (
            <span key={i} className="bg-brand-100 text-brand-700 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm">
              {reason}
            </span>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center">
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500 mb-1">Interests</div>
            <div className="font-bold text-brand-600 text-sm sm:text-base">{Math.round((career.matchDetails?.interests || 0) / 75 * 100)}%</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500 mb-1">Work Style</div>
            <div className="font-bold text-blue-600 text-sm sm:text-base">{Math.round((career.matchDetails?.workStyle || 0) / 30 * 100)}%</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-2 sm:p-3">
            <div className="text-xs text-gray-500 mb-1">Skills Fit</div>
            <div className="font-bold text-purple-600 text-sm sm:text-base">{Math.round((career.matchDetails?.skills || 0) / 32 * 100)}%</div>
          </div>
        </div>
      </div>

      {/* Why this might be for you */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600" />
          This might be for you if...
        </h3>
        <ul className="space-y-1.5 sm:space-y-2">
          {career.good_fit_if?.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-gray-700 text-sm sm:text-base">
              <span className="text-brand-500 mt-1">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Honest considerations */}
      <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 sm:p-6">
        <h3 className="font-bold text-amber-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5" />
          Consider carefully if...
        </h3>
        <ul className="space-y-1.5 sm:space-y-2">
          {career.not_good_fit_if?.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-amber-700 text-sm sm:text-base">
              <span className="mt-1">•</span>
              {item}
            </li>
          ))}
        </ul>
      </div>

      {/* Daily Tasks */}
      {career.daily_tasks && (
        <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
          <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 text-sm sm:text-base">What You'd Do Day-to-Day</h3>
          <ul className="space-y-1.5 sm:space-y-2">
            {career.daily_tasks.map((task, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700 text-sm sm:text-base">
                <CheckCircle className="w-4 h-4 text-brand-500 mt-1 flex-shrink-0" />
                {task}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Pathways */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600" />
          How to Get There
        </h3>
        <div className="space-y-3 sm:space-y-4">
          {career.pathways?.map((pathway, i) => (
            <div key={i} className={`p-3 sm:p-4 rounded-xl ${
              pathway.type === 'university' ? 'bg-blue-50 border border-blue-200' :
              pathway.type === 'tvet' ? 'bg-brand-50 border border-brand-200' :
              pathway.type === 'apprenticeship' ? 'bg-amber-50 border border-amber-200' :
              pathway.type === 'self_taught' ? 'bg-purple-50 border border-purple-200' :
              'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div>
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    pathway.type === 'university' ? 'bg-blue-200 text-blue-800' :
                    pathway.type === 'tvet' ? 'bg-brand-200 text-brand-800' :
                    pathway.type === 'apprenticeship' ? 'bg-amber-200 text-amber-800' :
                    pathway.type === 'self_taught' ? 'bg-purple-200 text-purple-800' :
                    'bg-gray-200 text-gray-800'
                  }`}>
                    {pathway.type === 'university' ? 'University Path' :
                     pathway.type === 'tvet' ? 'HEART/TVET Path' :
                     pathway.type === 'apprenticeship' ? 'Apprenticeship' :
                     pathway.type === 'diploma' ? 'Diploma Path' :
                     pathway.type === 'self_taught' ? 'Self-Taught Path' :
                     pathway.type === 'entry_level' ? 'Start Working' :
                     pathway.type === 'professional' ? 'Professional Certification' :
                     pathway.type}
                  </span>
                  <h4 className="font-bold text-gray-800 mt-2 text-sm sm:text-base">{pathway.name}</h4>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-xs sm:text-sm text-gray-600">{pathway.duration_years} {pathway.duration_years === 1 ? 'year' : 'years'}</div>
                </div>
              </div>

              <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                {pathway.institutions?.slice(0, 2).map((inst, j) => (
                  <div key={j} className="flex items-center gap-2 text-gray-600">
                    <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span className="truncate">{inst.name} - {inst.location}</span>
                  </div>
                ))}
              </div>

              {pathway.csec_required?.length > 0 && (
                <div className="mt-2 sm:mt-3">
                  <span className="text-xs font-medium text-gray-500">CSEC Required: </span>
                  <span className="text-xs sm:text-sm text-gray-700">{pathway.csec_required.join(', ')}</span>
                </div>
              )}

              {pathway.cost_range && (
                <div className="mt-1.5 sm:mt-2">
                  <span className="text-xs font-medium text-gray-500">Cost: </span>
                  <span className="text-xs sm:text-sm text-gray-700">
                    J${pathway.cost_range.min.toLocaleString()} - J${pathway.cost_range.max.toLocaleString()} {pathway.cost_range.per}
                  </span>
                  {pathway.cost_range.note && (
                    <span className="text-xs text-gray-500 ml-1">({pathway.cost_range.note})</span>
                  )}
                </div>
              )}

              {pathway.financial_aid?.length > 0 && (
                <div className="mt-1.5 sm:mt-2">
                  <span className="text-xs font-medium text-brand-600">Financial Aid: </span>
                  <span className="text-xs sm:text-sm text-gray-700">{pathway.financial_aid.join(', ')}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Salary */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600" />
          What You Can Earn in Jamaica
        </h3>
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="text-center p-2 sm:p-4 bg-gray-50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Starting</div>
            <div className="font-bold text-gray-800 text-xs sm:text-base">
              J${career.salary?.starting?.min?.toLocaleString()}-{career.salary?.starting?.max?.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400">/month</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-brand-50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Experienced</div>
            <div className="font-bold text-brand-700 text-xs sm:text-base">
              J${career.salary?.experienced?.min?.toLocaleString()}-{career.salary?.experienced?.max?.toLocaleString()}
            </div>
            <div className="text-xs text-gray-400">/month</div>
          </div>
          <div className="text-center p-2 sm:p-4 bg-amber-50 rounded-xl">
            <div className="text-xs text-gray-500 mb-1">Senior</div>
            <div className="font-bold text-amber-700 text-xs sm:text-base">
              J${(career.salary?.senior?.min || career.salary?.experienced?.max)?.toLocaleString()}+
            </div>
            <div className="text-xs text-gray-400">/month</div>
          </div>
        </div>
        {career.salary?.note && (
          <p className="text-xs sm:text-sm text-gray-500 mt-2 sm:mt-3">{career.salary.note}</p>
        )}
      </div>

      {/* Job Market */}
      <div className="bg-white rounded-xl shadow-sm border p-4 sm:p-6">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 flex items-center gap-2 text-sm sm:text-base">
          <Target className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600" />
          Job Market in Jamaica
        </h3>
        <div className="space-y-2 sm:space-y-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
              career.job_market?.demand === 'very_high' ? 'bg-brand-100 text-brand-700' :
              career.job_market?.demand === 'high' ? 'bg-brand-100 text-brand-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {career.job_market?.demand === 'very_high' ? 'Very High Demand' :
               career.job_market?.demand === 'high' ? 'High Demand' : 'Moderate Demand'}
            </span>
          </div>
          <p className="text-gray-700 text-sm sm:text-base">{career.job_market?.demand_description}</p>
          <p className="text-xs sm:text-sm text-gray-600"><strong>Locations:</strong> {career.job_market?.locations}</p>
          <p className="text-xs sm:text-sm text-gray-600"><strong>Growth:</strong> {career.job_market?.growth}</p>
          {career.job_market?.overseas && (
            <p className="text-xs sm:text-sm text-gray-600"><strong>Overseas:</strong> {career.job_market?.overseas}</p>
          )}
        </div>
      </div>

      {/* Real Talk */}
      <div className="bg-purple-50 rounded-xl border border-purple-200 p-4 sm:p-6">
        <h3 className="font-bold text-purple-800 mb-2 sm:mb-3 text-sm sm:text-base">Real Talk</h3>
        <p className="text-purple-700 text-sm sm:text-base">{career.real_talk}</p>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
        <button
          onClick={() => setPage('schools')}
          className="flex-1 py-2.5 sm:py-3 bg-brand-600 text-white rounded-xl font-semibold hover:bg-brand-700 text-sm sm:text-base"
        >
          Find Schools
        </button>
        <button
          onClick={() => setPage('funding')}
          className="flex-1 py-2.5 sm:py-3 bg-white border-2 border-brand-600 text-brand-600 rounded-xl font-semibold hover:bg-brand-50 text-sm sm:text-base"
        >
          Explore Funding
        </button>
      </div>
    </div>
  );

  // Results view
  const ResultsView = () => (
    <div className="space-y-4 sm:space-y-6" ref={resultsRef}>
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-brand-400 to-brand-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
          <Star className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
        </div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Your Career Matches</h2>
        <p className="text-sm sm:text-base text-gray-600 mt-2 px-2">Based on your interests, values, skills, and learning style, here are careers that could be a good fit for you.</p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-4 sm:mb-6 print:hidden">
        <button
          onClick={handlePrint}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <Printer className="w-4 h-4" />
          <span className="hidden xs:inline">Print</span>
        </button>
        <button
          onClick={restartQuiz}
          className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Start Over</span>
        </button>
      </div>

      {/* Results List */}
      <div className="space-y-3 sm:space-y-4">
        {results?.map((career, index) => (
          <button
            key={career.id}
            onClick={() => setSelectedCareer(career)}
            className="w-full bg-white rounded-xl p-3 sm:p-5 shadow-sm border border-gray-100 hover:shadow-md hover:border-brand-200 transition-all text-left"
          >
            <div className="flex items-start gap-2 sm:gap-4">
              <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex flex-col items-center justify-center text-white font-bold flex-shrink-0 ${
                index === 0 ? 'bg-gradient-to-br from-brand-500 to-brand-600' :
                index === 1 ? 'bg-gradient-to-br from-blue-500 to-indigo-600' :
                index === 2 ? 'bg-gradient-to-br from-purple-500 to-pink-600' :
                'bg-gradient-to-br from-gray-500 to-gray-600'
              }`}>
                <span className="text-xs">#{index + 1}</span>
                <span className="text-base sm:text-lg font-black">{career.matchPercentage}%</span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-gray-800 text-base sm:text-lg leading-tight">{career.title}</h3>
                <p className="text-xs sm:text-sm text-gray-500">{career.category}</p>
                <div className="mt-1.5 sm:mt-2 flex flex-wrap gap-1 sm:gap-2">
                  {career.matchReasons?.slice(0, 2).map((reason, i) => (
                    <span key={i} className="text-xs bg-brand-100 text-brand-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">
                      {reason}
                    </span>
                  ))}
                </div>
                <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-gray-600">
                  Starting: J${career.salary?.starting?.min?.toLocaleString()}-{career.salary?.starting?.max?.toLocaleString()}/mo
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0 hidden sm:block" />
            </div>
          </button>
        ))}
      </div>

      {/* Your Profile Summary */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 sm:p-6 mt-6 sm:mt-8">
        <h3 className="font-bold text-gray-800 mb-3 sm:mb-4 text-sm sm:text-base">Your Profile Summary</h3>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
          <div>
            <strong className="text-gray-600">Top Interests:</strong>
            <p className="text-gray-800">
              {answers.interests.map(i =>
                careerData.interest_areas.find(ia => ia.id === i)?.label
              ).join(', ') || 'Not selected'}
            </p>
          </div>
          <div>
            <strong className="text-gray-600">Work Environment:</strong>
            <p className="text-gray-800">
              {answers.environment === 'office' ? 'Office-based' :
               answers.environment === 'outdoor' ? 'Outdoor work' :
               answers.environment === 'varied' ? 'Varied locations' :
               answers.environment === 'home' ? 'Work from home' : 'Not selected'}
            </p>
          </div>
          <div>
            <strong className="text-gray-600">Career Priority:</strong>
            <p className="text-gray-800">
              {answers.stability === 'stable' ? 'Job stability' :
               answers.stability === 'growth' ? 'Growth potential' :
               answers.stability === 'flexible' ? 'Flexibility' :
               answers.stability === 'purpose' ? 'Meaningful work' : 'Not selected'}
            </p>
          </div>
          <div>
            <strong className="text-gray-600">Education Timeline:</strong>
            <p className="text-gray-800">
              {answers.education_time === 'quick' ? 'Quick start' :
               answers.education_time === 'medium' ? '2-3 years' :
               answers.education_time === 'long' ? '4+ years' :
               answers.education_time === 'parttime' ? 'Part-time' : 'Not selected'}
            </p>
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="bg-brand-50 rounded-xl p-4 sm:p-6">
        <h3 className="font-bold text-brand-800 mb-2 sm:mb-3 text-sm sm:text-base">Next Steps</h3>
        <ul className="space-y-1.5 sm:space-y-2 text-brand-700 text-xs sm:text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
            Click on any career above to learn more
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
            Talk to people working in careers that interest you
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
            Research training pathways and schools
          </li>
        </ul>
      </div>

      <div className="text-center text-xs sm:text-sm text-gray-500 px-2">
        <p>Remember: This is a starting point for exploration. Your career path is yours to shape.</p>
      </div>
    </div>
  );

  // Main render
  if (selectedCareer) {
    return (
      <div className="w-full">
        <CareerDetailView career={selectedCareer} />
      </div>
    );
  }

  if (results) {
    return (
      <div className="w-full">
        <BackButton onClick={goBack} />
        <ResultsView />
      </div>
    );
  }

  const steps = [
    WelcomeStep,          // 0
    InterestsStep,        // 1
    EnvironmentStep,      // 2
    PeopleStep,           // 3
    WorkValuesStep,       // 4
    WorkActivitiesStep,   // 5
    SkillsStep,           // 6
    LearningStyleStep,    // 7
    StabilityStep,        // 8
    EducationTimeStep     // 9
  ];
  const CurrentStep = steps[step];

  const canProceed = () => {
    if (step === 0) return true;
    if (step === 1) return answers.interests.length > 0;
    if (step === 2) return answers.environment !== null;
    if (step === 3) return answers.people !== null;
    if (step === 4) return true; // Work values optional
    if (step === 5) return true; // Work activities optional
    if (step === 6) return true; // Skills optional
    if (step === 7) return true; // Learning style optional
    if (step === 8) return answers.stability !== null;
    if (step === 9) return answers.education_time !== null;
    return false;
  };

  return (
    <div className="w-full">
      <BackButton onClick={step === 0 ? goBack : prevStep} label={step === 0 ? "Back" : "Previous"} />

      {step > 0 && <ProgressBar current={step} total={totalSteps - 1} sectionName={getCurrentSection()} />}

      <CurrentStep />

      {step > 0 && (
        <div className="flex justify-between mt-6 sm:mt-8">
          <button
            onClick={prevStep}
            className="px-4 sm:px-6 py-2.5 sm:py-3 text-gray-600 hover:text-gray-800 font-medium text-sm sm:text-base"
          >
            Previous
          </button>
          <button
            onClick={nextStep}
            disabled={!canProceed()}
            className={`px-5 sm:px-8 py-2.5 sm:py-3 rounded-xl font-semibold flex items-center gap-1.5 sm:gap-2 text-sm sm:text-base ${
              canProceed()
                ? 'bg-brand-600 text-white hover:bg-brand-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {step === totalSteps - 1 ? 'See My Results' : 'Continue'}
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CareerPathVisualiser;
